/**
 * Key-value derivation for GAM requests. Seven keys, all plain words:
 *
 *   section   multi-value: top slug + "top_sub" (derived from the URL)
 *   page      home | listing | article | video | quote | search | other
 *   usertype  nologin | free | paid   (cookie `ej_usertype`, set by the auth backend)
 *   channel   direct | search | social | email | referral (first touch per session)
 *   keyword   article tags / meta keywords
 *   stockcode HKEX code on Stock360 quote / index pages
 *   tier      M02 … M13 — the subscriber lifecycle bucket the legacy entitlement backend
 *             writes to the domain cookie `HKEJ004` (only sent when the cookie holds one).
 *             The legacy home page picked one of 13 `HKEJ_HomePage_Subscriber_Textbox` units
 *             from it; here one SponsorBanner unit + line items targeting `tier` does the same.
 *             Buckets (entitle MemberController.php, 2016-09-26 edit):
 *               M02 lapsed > 31 days        M03 subscribed via an agent
 *               M04 lapsed ≤ 31 days        M05 no auto-renew, expires in 61–90 days
 *               M06 no auto-renew, 11–60 d  M07 no auto-renew, ≤ 10 days
 *               M08 no auto-renew, > 90 d   M09 auto-renew, card expires first, 61–90 d
 *               M10 auto-renew, card first, 11–60 d   M11 auto-renew, card first, ≤ 10 d
 *               M12 auto-renew, card valid (or > 90 d)   M13 HKEJ staff / VIP
 *             The cookie must be JS-readable (not HttpOnly) and set for the revamp domain
 *             before the first ad call; no cookie → no `tier` key (the legacy default strip).
 *
 * `section` values follow the live hkej.com routes (the revamp routes will be
 * renamed to them): kebab-case, sub-section joined with "_", e.g.
 * online-news, daily-news, property + property_firsthand, multimedia_finance.
 */
import { BRAND_PREFIXES, NO_AD_PREFIXES, isLivePage, kvKey, type Brand } from "./config";

export type PageType = "home" | "listing" | "article" | "video" | "quote" | "search" | "other";
export type UserType = "nologin" | "free" | "paid";
export type Channel = "direct" | "search" | "social" | "email" | "referral";

export interface PageTargeting {
  brand: Brand;
  section: string[];
  page: PageType;
  keyword?: string[];
  stockcode?: string;
  /** false on account / auth / checkout / legal / ratecard / e-paper routes */
  adsAllowed: boolean;
}

const LOCALE_RE = /^\/(en|zh-CN|zh-TW)(?=\/|$)/;

/** GAM reserved characters in key-value values. */
const RESERVED = /["'=!+#*&;^()<>\[\],~]/g;
export const MAX_VALUE_LEN = 40;
export const MAX_KEYWORDS = 20;

export function cleanValue(v: string): string {
  return v.replace(RESERVED, "").trim().slice(0, MAX_VALUE_LEN);
}

/** EJ Education's percent-encoded Chinese category slugs → ASCII. */
const EJEDU_SLUGS: Record<string, string> = {
  最新消息: "edu-news",
  教育資訊: "edu-info",
  名家專欄: "edu-columns",
  校長訪談: "edu-principal",
  直資人語: "edu-dss",
  親子教育: "edu-parenting",
  幼小學府: "edu-schools",
  自由博客: "edu-blog",
};

export function stripLocale(pathname: string): string {
  return pathname.replace(LOCALE_RE, "") || "/";
}

export function brandFromPath(path: string): Brand {
  for (const [prefix, brand] of BRAND_PREFIXES) {
    if (path === prefix || path.startsWith(prefix + "/")) return brand;
  }
  return "HKEJ";
}

function brandPrefix(path: string): string {
  for (const [prefix] of BRAND_PREFIXES) {
    if (path === prefix || path.startsWith(prefix + "/")) return prefix;
  }
  return "";
}

/**
 * Current revamp route slugs that differ from the live hkej.com section names the
 * `section` values follow. Temporary: once the routes are renamed for SEO the URL and
 * the legacy name coincide and this table empties.
 */
const SECTION_ALIAS: Record<string, string> = {
  dailynews: "daily-news",
  "live-news": "online-news",
  video: "multimedia",
};

function slugify(seg: string): string {
  let s = seg;
  try {
    s = decodeURIComponent(seg);
  } catch {
    /* keep raw */
  }
  if (EJEDU_SLUGS[s]) return EJEDU_SLUGS[s];
  return cleanValue(s.toLowerCase().replace(/\s+/g, "-"));
}

/** Route → { section[], page }. Pure; safe on the server. */
export function deriveFromPath(pathname: string): Pick<PageTargeting, "brand" | "section" | "page" | "adsAllowed"> {
  const path = stripLocale(pathname.split("?")[0]);
  const brand = brandFromPath(path);
  const adsAllowed = !NO_AD_PREFIXES.some((p) => path === p || path.startsWith(p + "/")) && isLivePage(path);
  const rel = path.slice(brandPrefix(path).length) || "/";
  const segs = rel.split("/").filter(Boolean);

  if (segs.length === 0) return { brand, section: ["home"], page: "home", adsAllowed };

  // Page type from the route shape.
  let page: PageType = "listing";
  const first = segs[0];
  const joined = rel.toLowerCase();
  if (/article/.test(joined) || /\/articles\//.test(joined) || /\/(reports|knowledge)\/[^/]+$/.test(joined)) page = "article";
  else if (first === "search") page = "search";
  else if (/^\/video(\/|$)/.test(joined)) page = segs.length > 1 ? "video" : "listing";
  else if (/^\/(quote|indices)\/[^/]+$/.test(joined)) page = "quote";
  else if (/^\/(portfolio|upgrade|calculator|chart)$/.test(joined) || /\/(calculator|chart)$/.test(joined)) page = "other";

  // Section: first segment, plus first_second when a sub-section exists.
  const top = SECTION_ALIAS[slugify(first)] ?? slugify(first);
  const section: string[] = [];
  if (page === "article" && segs.length === 1) {
    // bare article routes carry no section of their own
  } else if (top) {
    section.push(top);
    const second = segs[1];
    if (second && !/^\d+$/.test(second) && page !== "quote" && page !== "article" && !/^\[/.test(second)) {
      const sub = slugify(second);
      if (sub && sub !== top) section.push(`${top}_${sub}`);
    }
  }
  return { brand, section, page, adsAllowed };
}

/* ───────────── session-scoped keys (browser only) ───────────── */

export const USERTYPE_COOKIE = "ej_usertype";
export const CHANNEL_STORAGE_KEY = "ej_channel";
export const TEST_STORAGE_KEY = "ej_adtest";
/** `?adtest=<name>` (kept for the session; `?adtest=0` clears) → key `test`: a demo picks one test line
 *  item (billboard, pushdown, cube, interscroller, outstream) deterministically instead of rotating
 *  with the House rows. Never set in production traffic — the key only exists on the test tree. */
export function readAdTest(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const q = new URLSearchParams(window.location.search).get("adtest");
    if (q !== null) {
      if (q === "0" || q === "") window.sessionStorage.removeItem(TEST_STORAGE_KEY);
      else window.sessionStorage.setItem(TEST_STORAGE_KEY, cleanValue(q));
    }
    return window.sessionStorage.getItem(TEST_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}
export const TIER_COOKIE = "HKEJ004";
export type Tier = "M02" | "M03" | "M04" | "M05" | "M06" | "M07" | "M08" | "M09" | "M10" | "M11" | "M12" | "M13";

/** Subscriber lifecycle bucket from the legacy `HKEJ004` cookie; undefined when absent or not M02–M13. */
export function readTier(): Tier | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${TIER_COOKIE}=(M(?:0[2-9]|1[0-3]))(?:;|$)`));
  return (m?.[1] as Tier) ?? undefined;
}

export function readUserType(): UserType {
  if (typeof document === "undefined") return "nologin";
  const m = document.cookie.match(new RegExp(`(?:^|; )${USERTYPE_COOKIE}=(nologin|free|paid)`));
  return (m?.[1] as UserType) ?? "nologin";
}

const SEARCH_HOSTS = /(^|\.)(google\.[a-z.]+|bing\.com|yahoo\.[a-z.]+|baidu\.com|duckduckgo\.com|ecosia\.org|sogou\.com|so\.com)$/;
const SOCIAL_HOSTS = /(^|\.)(facebook\.com|fb\.com|instagram\.com|t\.co|twitter\.com|x\.com|linkedin\.com|threads\.net|youtube\.com|youtu\.be|whatsapp\.com|telegram\.org|t\.me|line\.me|wechat\.com|weixin\.qq\.com|weibo\.com|reddit\.com|pinterest\.com)$/;

export function classifyChannel(referrer: string, search: string, currentHost: string): Channel {
  const params = new URLSearchParams(search);
  const medium = (params.get("utm_medium") ?? "").toLowerCase();
  const source = (params.get("utm_source") ?? "").toLowerCase();
  if (medium === "email" || medium === "edm" || medium === "newsletter") return "email";
  if (medium === "social" || /facebook|instagram|twitter|linkedin|threads|whatsapp|telegram|line|wechat|weibo/.test(source)) return "social";
  if (medium === "cpc" || medium === "ppc" || medium === "organic" || /google|bing|yahoo|baidu/.test(source)) return "search";
  if (medium === "referral") return "referral";
  if (!referrer) return "direct";
  let host = "";
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return "direct";
  }
  if (host === currentHost || host.endsWith(".hkej.com") || host.endsWith("hkej.com")) return "direct";
  if (SEARCH_HOSTS.test(host)) return "search";
  if (SOCIAL_HOSTS.test(host)) return "social";
  return "referral";
}

/** First-touch channel per session, remembered in sessionStorage. */
export function readChannel(): Channel {
  if (typeof window === "undefined") return "direct";
  try {
    const stored = window.sessionStorage.getItem(CHANNEL_STORAGE_KEY) as Channel | null;
    if (stored) return stored;
  } catch {
    /* storage blocked */
  }
  const ch = classifyChannel(document.referrer, window.location.search, window.location.hostname);
  try {
    window.sessionStorage.setItem(CHANNEL_STORAGE_KEY, ch);
  } catch {
    /* ignore */
  }
  return ch;
}

export function cleanKeywords(list: readonly string[] | undefined): string[] {
  if (!list) return [];
  const out: string[] = [];
  for (const k of list) {
    const v = cleanValue(k);
    if (v && !out.includes(v)) out.push(v);
    if (out.length >= MAX_KEYWORDS) break;
  }
  return out;
}

/** The complete `cust_params` for a page, as key → value(s). */
export function buildTargeting(t: PageTargeting, usertype: UserType, channel: Channel, env?: string, tier?: Tier, test?: string): Record<string, string | string[]> {
  const kv: Record<string, string | string[]> = {
    [kvKey("section")]: t.section.map(cleanValue).filter(Boolean),
    [kvKey("page")]: t.page,
    [kvKey("usertype")]: usertype,
    [kvKey("channel")]: channel,
  };
  const kw = cleanKeywords(t.keyword);
  if (kw.length) kv[kvKey("keyword")] = kw;
  if (t.stockcode) kv[kvKey("stockcode")] = cleanValue(t.stockcode);
  if (env) kv[kvKey("env")] = env;
  if (tier) kv[kvKey("tier")] = tier;
  if (test) kv[kvKey("test")] = test;
  return kv;
}
